import { useState, useEffect, FC, CSSProperties } from 'react';
import {
  ShieldCheck,
  MessageCircle,
  Users,
  Zap,
  CheckCircle2,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import {
  PublicBridgePageData,
  fetchPublicBridgePage,
  recordPublicBridgeClick,
} from '../../api/publicBridge';
import { Spinner } from '../ui/Spinner';

export interface PublicBridgeViewProps {
  slug: string;
  initialData?: PublicBridgePageData | null;
}

export const PublicBridgeView: FC<PublicBridgeViewProps> = ({
  slug,
  initialData = null,
}) => {
  const [data, setData] = useState<PublicBridgePageData | null>(initialData);
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState<boolean>(false);
  const [timeLeft, setTimeLeft] = useState<number>(() => {
    return initialData ? initialData.timer_seconds : 180;
  });

  // 1. Carrega os dados da página ponte pública e transbordo
  useEffect(() => {
    if (initialData) return;

    let isMounted = true;
    setLoading(true);
    setError(null);

    fetchPublicBridgePage(slug)
      .then((page) => {
        if (isMounted) {
          setData(page);
          setTimeLeft(page.timer_seconds);
          setLoading(false);

          // Inicializa Meta Pixel e GA4 dinamicamente se configurados
          if (page.meta_pixel_id && typeof window !== 'undefined') {
            initMetaPixel(page.meta_pixel_id);
          }
          if (page.ga4_measurement_id && typeof window !== 'undefined') {
            initGA4(page.ga4_measurement_id);
          }
        }
      })
      .catch((err: unknown) => {
        if (isMounted) {
          const msg =
            err instanceof Error ? err.message : 'Página não encontrada';
          setError(msg);
          setLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [slug, initialData]);

  // 2. Contador decrescente de escassez
  useEffect(() => {
    if (timeLeft <= 0) return;
    const interval = setInterval(() => {
      setTimeLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => clearInterval(interval);
  }, [timeLeft]);

  // 3. Inicializador dinâmico de Meta Pixel
  const initMetaPixel = (pixelId: string) => {
    try {
      const win = window as unknown as { fbq?: (...args: unknown[]) => void };
      if (!win.fbq) {
        /* eslint-disable */
        (function (f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
          if (f.fbq) return;
          n = f.fbq = function () {
            n.callMethod
              ? n.callMethod.apply(n, arguments)
              : n.queue.push(arguments);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = !0;
          n.version = '2.0';
          n.queue = [];
          t = b.createElement(e);
          t.async = !0;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t, s);
        })(
          window,
          document,
          'script',
          'https://connect.facebook.net/en_US/fbevents.js'
        );
        /* eslint-enable */
      }
      if (win.fbq) {
        win.fbq('init', pixelId);
        win.fbq('track', 'PageView');
      }
    } catch {
      // Ignora erro de bloqueio de terceiros/adblock
    }
  };

  // 4. Inicializador dinâmico de GA4
  const initGA4 = (gaId: string) => {
    try {
      const win = window as unknown as {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
      };
      if (!win.gtag) {
        win.dataLayer = win.dataLayer || [];
        win.gtag = function () {
          // eslint-disable-next-line prefer-rest-params
          win.dataLayer?.push(arguments);
        };
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(gaId)}`;
        document.head.appendChild(script);
        win.gtag('js', new Date());
        win.gtag('config', gaId);
      }
    } catch {
      // Ignora erro
    }
  };

  // 5. Tratamento de clique no CTA
  const handleJoinClick = async () => {
    if (!data || redirecting) return;
    setRedirecting(true);

    const eventId = `lead_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    const eventSourceUrl =
      typeof window !== 'undefined' ? window.location.href : undefined;

    // Eventos de rastreamento no client com suporte a deduplicação Meta CAPI (eventID)
    try {
      const win = window as unknown as {
        fbq?: (
          action: string,
          event: string,
          params?: unknown,
          options?: unknown
        ) => void;
        gtag?: (action: string, event: string, params?: unknown) => void;
      };
      if (win.fbq) {
        win.fbq(
          'track',
          'Lead',
          {
            content_name: data.title,
            currency: 'BRL',
            value: 0.0,
          },
          { eventID: eventId }
        );
        win.fbq(
          'trackCustom',
          'ClickToWhatsAppGroup',
          {
            slug: data.slug,
            group_id: data.active_group_id,
          },
          { eventID: eventId }
        );
      }
      if (win.gtag) {
        win.gtag('event', 'join_whatsapp_group', {
          event_category: 'engagement',
          event_label: data.slug,
        });
      }
    } catch {
      // Ignora erros de telemetria client
    }

    // Registra clique/conversão no backend assincronamente e dispara Meta CAPI server-side
    await recordPublicBridgeClick(data.slug, data.active_group_id, {
      eventId,
      eventSourceUrl,
    });

    // Redireciona para o grupo de WhatsApp ativo
    const targetUrl =
      data.active_group_invite_link || 'https://chat.whatsapp.com/';
    if (typeof window !== 'undefined') {
      window.location.href = targetUrl;
    }
  };

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  // Renderização de tela de Carregamento
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas p-4">
        <div className="flex flex-col items-center space-y-3 text-center">
          <Spinner size="lg" color="brand" />
          <p className="text-xs font-medium text-content-muted">
            Localizando grupo oficial com vagas...
          </p>
        </div>
      </div>
    );
  }

  // Renderização de Erro / Não Encontrada
  if (error || !data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-surface-canvas p-4">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-surface-border bg-surface-panel p-8 text-center shadow-sm">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-400">
            <MessageCircle className="h-6 w-6" />
          </div>
          <div className="space-y-1">
            <h2 className="text-base font-semibold text-content-main">
              Página não disponível
            </h2>
            <p className="text-xs leading-relaxed text-content-muted">
              {error ||
                'Esta página de captação está temporariamente pausada ou foi desativada.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Cores dinâmicas injetadas via CSS vars no escopo deste componente
  const dynamicBrandStyle: CSSProperties = {
    ['--brand-custom' as string]: data.primary_color,
    ['--brand-secondary-custom' as string]: data.secondary_color,
  };

  const currentMembers = data.current_members;
  const maxCapacity = data.max_capacity > 0 ? data.max_capacity : 1000;
  const remainingSpots = Math.max(0, maxCapacity - currentMembers);
  const fillPercentage = Math.min(
    100,
    Math.round((currentMembers / maxCapacity) * 100)
  );

  // Urgência contextual: apenas exibir contador regressivo se a ocupação for relevante (>= 75%)
  const showUrgencyTimer =
    timeLeft > 0 && maxCapacity > 0 && currentMembers / maxCapacity >= 0.75;

  // Resolução de tema da página pública com suporte à customização de temas
  const activeTheme = data.background_theme || data.theme_preference;
  let themeClass = 'bg-surface-canvas text-content-main';
  if (activeTheme === 'light') {
    themeClass = 'bg-slate-50 text-slate-900';
  } else if (activeTheme === 'emerald') {
    themeClass = 'dark bg-emerald-950 text-emerald-50';
  } else if (activeTheme === 'indigo') {
    themeClass = 'dark bg-indigo-950 text-indigo-50';
  } else if (activeTheme === 'slate') {
    themeClass = 'dark bg-slate-900 text-slate-100';
  } else if (activeTheme === 'dark') {
    themeClass = 'dark bg-slate-950 text-slate-100';
  }

  return (
    <div
      style={dynamicBrandStyle}
      className={`flex min-h-screen flex-col justify-between px-4 py-6 font-sans antialiased selection:bg-brand-500 selection:text-white sm:px-6 md:py-10 ${themeClass}`}
    >
      {/* Header Institucional */}
      <header className="relative mx-auto w-full max-w-lg text-center">
        <div className="flex flex-col items-center gap-2">
          {data.logo_url ? (
            <img
              src={data.logo_url}
              alt={data.tenant_name}
              className="h-10 max-w-[180px] object-contain"
            />
          ) : (
            <span className="text-xs font-bold uppercase tracking-wider text-content-muted">
              {data.tenant_name}
            </span>
          )}
        </div>
      </header>

      {/* Cartão Central de Captura e Conversão */}
      <main className="mx-auto my-auto w-full max-w-lg py-6">
        <div className="relative overflow-hidden rounded-3xl border border-surface-border bg-surface-panel p-6 shadow-xl transition-all sm:p-8">
          {/* Faixa de Urgência Regressiva Contextual */}
          {showUrgencyTimer && (
            <div className="-mx-6 -mt-6 mb-6 border-b border-surface-border bg-surface-subtle p-3 text-center sm:-mx-8 sm:-mt-8">
              <p className="flex items-center justify-center gap-1.5 text-xs font-semibold text-brand-600 dark:text-brand-400">
                <Zap className="h-4 w-4 fill-brand-500 text-brand-500" />
                Últimas vagas disponíveis encerrando em:{' '}
                <span className="rounded border border-surface-border bg-surface-panel px-2 py-0.5 font-mono font-bold text-content-main">
                  {`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`}
                </span>
              </p>
            </div>
          )}

          {/* Headline & Subheadline */}
          <div className="space-y-3 text-center">
            <h1 className="text-xl font-extrabold tracking-tight text-content-main sm:text-2xl sm:leading-snug">
              {data.headline_text || data.headline}
            </h1>
            {data.subheadline && (
              <p className="text-xs leading-relaxed text-content-muted sm:text-sm">
                {data.subheadline}
              </p>
            )}
          </div>

          {/* Status do Grupo e Barra de Ocupação */}
          <div className="my-6 space-y-2.5 rounded-2xl border border-surface-border bg-surface-subtle p-4">
            <div className="flex items-center justify-between text-xs font-medium text-content-main">
              <span className="flex items-center gap-1.5 truncate font-semibold text-brand-600 dark:text-brand-400">
                <Users className="h-4 w-4 shrink-0" />
                <span className="truncate">
                  {data.active_group_name || 'Grupo VIP Oficial'}
                </span>
              </span>
              <span className="font-mono text-2xs text-content-muted">
                <strong className="text-content-main">{currentMembers}</strong>
                {` / ${maxCapacity} vagas`}
                {remainingSpots > 0 && ` (${remainingSpots} vagas restantes)`}
              </span>
            </div>

            {/* Barra de Progresso com Transição Suave */}
            <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-border">
              <div
                className="h-full rounded-full bg-brand-500 transition-all duration-700"
                style={{ width: `${fillPercentage}%` }}
              />
            </div>

            <p className="flex items-center justify-center gap-1 text-center text-micro text-content-muted">
              <ShieldCheck className="h-3.5 w-3.5 text-brand-500" />
              Ambiente verificado e seguro
            </p>
          </div>

          {/* Argumentos de Valor e Confiança */}
          <ul className="mb-6 space-y-2 text-xs text-content-main">
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-500" />
              <span>Acesso prioritário a cupons e promoções auditadas</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-500" />
              <span>Sem spam: ofertas selecionadas em tempo real</span>
            </li>
            <li className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 shrink-0 text-brand-500" />
              <span>Grupo oficial com moderação ativa</span>
            </li>
          </ul>

          {/* Botão de Ação Primária CTA com Cor Personalizável */}
          <button
            type="button"
            onClick={handleJoinClick}
            disabled={redirecting}
            style={{
              backgroundColor: data.button_color || '#25D366',
            }}
            className="group relative flex w-full cursor-pointer items-center justify-center gap-2 rounded-2xl px-6 py-3.5 text-sm font-bold text-white shadow-md transition-all duration-150 hover:brightness-95 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:text-base"
          >
            {redirecting ? (
              <>
                <Spinner size="sm" color="white" />
                <span>ENTRANDO NO GRUPO...</span>
              </>
            ) : (
              <>
                <MessageCircle className="h-5 w-5 fill-white stroke-none" />
                <span>{data.button_text}</span>
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
              </>
            )}
          </button>

          {/* Link Opcional para Vitrine Pública de Ofertas */}
          {data.show_showcase_link && (
            <div className="mt-4 text-center">
              <a
                href={
                  data.tenant_slug
                    ? `/showcase/${data.tenant_slug}`
                    : '/showcase'
                }
                className="inline-flex items-center gap-1.5 text-xs font-medium text-content-muted transition-colors hover:text-brand-500 hover:underline"
              >
                <span>Ou veja todas as ofertas recentes na vitrine</span>
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          )}
        </div>
      </main>

      {/* Rodapé Discreto com Co-Branding TrafficForge */}
      <footer className="relative mx-auto w-full max-w-lg text-center text-micro text-content-muted">
        <div className="flex flex-col items-center justify-center gap-1">
          <p>
            © {new Date().getFullYear()} {data.tenant_name}. Todos os direitos
            reservados.
          </p>
          <div className="flex items-center gap-3">
            {data.privacy_policy_url && (
              <a
                href={data.privacy_policy_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-0.5 hover:text-content-main hover:underline"
              >
                Política de Privacidade
                <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
            <span>•</span>
            <span className="font-semibold text-content-muted">
              Powered by TrafficForge
            </span>
          </div>
          <p className="text-content-muted/80 mt-1 text-2xs">
            Este site não possui vínculo oficial com o WhatsApp ou Meta Inc.
          </p>
        </div>
      </footer>
    </div>
  );
};
